/** @format */

import { LeftOutlined, LockOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import { Alert, Button, Segmented, Select, Spin } from "antd";
import moment from "moment";
import React, { useMemo } from "react";
import { TUser } from "../../../../redux-modules/declarations/maps/auth";
import {
  TGiornoPresenze,
  TPersonaPresenze,
  TPresenzeMese,
} from "../../../../redux-modules/declarations/maps/presenzeVista";
import {
  chipSegnalazione,
  contestiDelGiorno,
  descrizioneContrattoGiorno,
  diUnAltroGiorno,
  eStraordinario,
  etichettaMese,
  etichettaStatoTurno,
  formatDurata,
  livelloPresenza,
  nomeCompleto,
  ora,
  orario,
  plurale,
  tinta,
  TRATTINO,
  TUnitaPresenze,
} from "../format";
import { BarraStrumenti, MeseStile } from "../styles";
import { TLettura } from "../usePresenze";
import { AvatarPersona } from "./AvatarPersona";

interface Props {
  lettura: TLettura<TPresenzeMese>;
  dipendente: number | null;
  mese: string;
  utenti: TUser[] | null;
  unita: TUnitaPresenze;
  onDipendente: (id: number) => void;
  onMese: (mese: string) => void;
  onUnita: (unita: TUnitaPresenze) => void;
  onApriGiorno: (persona: TPersonaPresenze, data: string) => void;
  /** giorno aperto nel pannello, evidenziato nella tabella */
  giornoAperto?: string | null;
}

/** Scostamento delle timbrature iniziate nel giorno, per stato della banca ore. */
const scostamentoGiorno = (
  g: TGiornoPresenze,
): { minuti: number; inAttesa: boolean; rifiutate: boolean; presenti: boolean } => {
  let minuti = 0;
  let inAttesa = false;
  let rifiutate = false;
  let presenti = false;
  g.presenze
    .filter(p => !p.giorno_prima && p.timebank)
    .forEach(p => {
      const tb = p.timebank;
      if (!tb) return;
      presenti = true;
      if (tb.stato === "rifiutata") {
        rifiutate = true;
        return;
      }
      minuti += tb.delta_minuti;
      if (tb.stato === "in_attesa") inAttesa = true;
    });
  return { minuti, inAttesa, rifiutate, presenti };
};

export const Mese = ({
  lettura,
  dipendente,
  mese,
  utenti,
  unita,
  onDipendente,
  onMese,
  onUnita,
  onApriGiorno,
  giornoAperto = null,
}: Props): JSX.Element => {
  const { dati, caricamento, aggiornamento, errore, ricarica } = lettura;
  const m = moment(`${mese}-01`);

  const opzioni = useMemo(
    () =>
      (utenti || [])
        .filter(u => !u.is_disabled)
        .map(u => ({ value: Number(u.id), label: `${u.surname} ${u.name}`.trim() }))
        .sort((a, b) => a.label.localeCompare(b.label, "it")),
    [utenti],
  );

  const persona = dati?.persona || null;
  const modalita = dati?.modalita_presenze || "banca_ore";

  return (
    <MeseStile>
      <BarraStrumenti>
        <div className="riga">
          {persona ? (
            <div className="persona">
              <AvatarPersona persona={persona} grande />
              <div>
                <div className="nome">{nomeCompleto(persona)}</div>
                <div className="gruppo">
                  {[persona.gruppo, persona.reparto].filter(Boolean).join(", ") || "Senza gruppo"}
                </div>
              </div>
            </div>
          ) : null}
          <Select
            className="scelta-persona"
            showSearch
            optionFilterProp="label"
            placeholder={persona ? "Cambia persona" : "Scegli la persona"}
            aria-label="Persona"
            loading={!utenti}
            value={undefined}
            options={opzioni}
            notFoundContent={utenti ? "Nessuna persona" : "Caricamento…"}
            onChange={(v: number): void => onDipendente(v)}
          />
          <div className="settimana">
            <Button
              icon={<LeftOutlined />}
              aria-label="Mese precedente"
              onClick={(): void => onMese(m.clone().subtract(1, "month").format("YYYY-MM"))}
            />
            <span className="mese">{etichettaMese(mese)}</span>
            <Button
              icon={<RightOutlined />}
              aria-label="Mese successivo"
              onClick={(): void => onMese(m.clone().add(1, "month").format("YYYY-MM"))}
            />
            <Button onClick={(): void => onMese(moment().format("YYYY-MM"))}>Questo mese</Button>
          </div>
          <div className="spazio" />
          {aggiornamento && <Spin size="small" />}
          <Segmented
            aria-label="Unità delle ore"
            value={unita}
            options={[
              { label: "Ore e minuti", value: "ore" },
              { label: "Centesimi", value: "centesimi" },
            ]}
            onChange={(v): void => onUnita(v as TUnitaPresenze)}
          />
        </div>
      </BarraStrumenti>

      {errore && (
        <Alert
          type="error"
          showIcon
          message={errore}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={ricarica}>
              Riprova
            </Button>
          }
        />
      )}

      {!dipendente && (
        <div className="vuoto">
          <strong>Scegli una persona per vedere il suo mese.</strong>
          <div className="attenuato">
            Dalla Giornata o da Da gestire si arriva qui anche dal pannello della persona.
          </div>
        </div>
      )}

      {dipendente && (
        <Spin spinning={caricamento}>
          {dati && persona ? (
            <ContenutoMese
              dati={dati}
              unita={unita}
              modalita={modalita}
              onApriGiorno={onApriGiorno}
              giornoAperto={giornoAperto}
            />
          ) : (
            <div style={{ minHeight: 320 }} />
          )}
        </Spin>
      )}
    </MeseStile>
  );
};

interface PropsContenuto {
  dati: TPresenzeMese;
  unita: TUnitaPresenze;
  modalita: "banca_ore" | "straordinario";
  onApriGiorno: (persona: TPersonaPresenze, data: string) => void;
  giornoAperto: string | null;
}

const ContenutoMese = ({
  dati,
  unita,
  modalita,
  onApriGiorno,
  giornoAperto,
}: PropsContenuto): JSX.Element => {
  const { totali, contratto, persona } = dati;
  const oggi = dati.mese.oggi;
  const meseInCorso = oggi >= dati.mese.dal && oggi <= dati.mese.al;
  const previste = meseInCorso ? totali.minuti_previsti_ad_oggi : totali.minuti_previsti;
  const percentuale =
    previste > 0 ? Math.min(100, Math.round((totali.minuti_lavorati / previste) * 100)) : 0;
  const etichettaOre = meseInCorso
    ? `Ore lavorate, dal ${moment(dati.mese.dal).format("D")} al ${moment(oggi).format("D MMMM")}`
    : "Ore lavorate nel mese";
  const tb = totali.timebank;
  const giustificativi = totali.giustificativi.filter(g => g.giorni > 0 || g.minuti > 0);
  const inAttesa = totali.giustificativi.reduce((n, g) => n + g.in_attesa, 0);

  return (
    <>
      <div className="contratto">
        {contratto.versioni.length === 1 ? (
          <>
            <span>Contratto in vigore nel mese:</span>
            <b>{descrizioneContrattoGiorno(contratto.versioni[0])}</b>
          </>
        ) : (
          <>
            <span>Il contratto cambia nel mese:</span>
            {contratto.versioni.map(v => (
              <span key={v.dal}>
                dal {moment(v.dal).format("D")} al {moment(v.al).format("D MMMM")}:{" "}
                <b>{descrizioneContrattoGiorno(v)}</b>
              </span>
            ))}
          </>
        )}
      </div>

      <section className="riepilogo" aria-label="Totali del mese">
        <div className="riquadro">
          <span className="chiave">{etichettaOre}</span>
          <span className="valore">
            {formatDurata(totali.minuti_lavorati, unita)}{" "}
            <span>di {formatDurata(previste, unita)} previste</span>
          </span>
          <div className="barra" aria-hidden="true">
            <span
              className={percentuale >= 100 ? "piena" : undefined}
              style={{ width: `${percentuale}%` }}
            />
          </div>
        </div>
        <div className="riquadro">
          <span className="chiave">
            {modalita === "banca_ore" ? "Banca ore del mese" : "Scostamenti del mese"}
          </span>
          <span className="valore">
            {formatDurata(tb.approvata_minuti, unita, true)} <span>approvate</span>
          </span>
          <span className="nota">
            {tb.in_attesa > 0
              ? `${plurale(
                  tb.in_attesa,
                  "timbratura da approvare",
                  "timbrature da approvare",
                )} (${formatDurata(tb.in_attesa_minuti, unita, true)})`
              : "Nessuna timbratura da approvare"}
            {tb.rifiutata_minuti !== 0
              ? `; rifiutate ${formatDurata(tb.rifiutata_minuti, unita, true)}`
              : ""}
          </span>
        </div>
        <div className="riquadro">
          <span className="chiave">Giustificativi del mese</span>
          <span className="valore">
            {giustificativi.length ? (
              giustificativi.map((g, i) => (
                <React.Fragment key={g.tipo_id}>
                  {i > 0 && <span>, </span>}
                  {g.unita === "GIORNI" ? `${g.giorni} g` : formatDurata(g.minuti, unita)}{" "}
                  <span>{g.nome.toLowerCase()}</span>
                </React.Fragment>
              ))
            ) : (
              <span>nessuno accettato</span>
            )}
          </span>
          <span className="nota">
            {inAttesa > 0
              ? plurale(inAttesa, "richiesta in attesa", "richieste in attesa")
              : "Nessuna richiesta in attesa"}
          </span>
        </div>
        <div className="riquadro">
          <span className="chiave">Da gestire</span>
          <span className="valore">
            {totali.anomalie}{" "}
            <span>
              {totali.anomalie === 1 ? "anomalia" : "anomalie"}
              {totali.giorni_con_anomalie > 0
                ? ` su ${plurale(totali.giorni_con_anomalie, "giorno", "giorni")}`
                : ""}
            </span>
          </span>
          <span className="nota">
            {totali.avvisi > 0 ? plurale(totali.avvisi, "avviso", "avvisi") : "Nessun avviso"}
          </span>
        </div>
      </section>

      <div className="tabella">
        <table>
          <thead>
            <tr>
              <th scope="col">Giorno</th>
              <th scope="col">Turni previsti</th>
              <th scope="col">Timbrature</th>
              <th scope="col" className="num">
                Ore lavorate
              </th>
              <th scope="col" className="num">
                {modalita === "banca_ore" ? "Banca ore" : "Scostamento"}
              </th>
              <th scope="col">Giustificativi</th>
              <th scope="col">Da gestire</th>
              <th scope="col">
                <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
                  Azioni
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {dati.giorni.map(g => (
              <RigaGiorno
                key={g.data}
                giorno={g}
                persona={persona}
                oggi={oggi}
                unita={unita}
                modalita={modalita}
                aperto={g.data === giornoAperto}
                onApri={(): void => onApriGiorno(persona, g.data)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

interface PropsRiga {
  giorno: TGiornoPresenze;
  persona: TPersonaPresenze;
  oggi: string;
  unita: TUnitaPresenze;
  modalita: "banca_ore" | "straordinario";
  aperto: boolean;
  onApri: () => void;
}

const RigaGiorno = ({
  giorno: g,
  persona,
  oggi,
  unita,
  modalita,
  aperto,
  onApri,
}: PropsRiga): JSX.Element => {
  const m = moment(g.data);
  const classi = [
    g.data === oggi ? "oggi" : "",
    g.festivita ? "festa" : "",
    !g.previsto ? "non-previsto" : "",
    aperto ? "aperto" : "",
  ]
    .filter(Boolean)
    .join(" ");
  // le segnalazioni delle presenze iniziate il giorno prima sono già sulla riga di quel giorno
  const contesti = contestiDelGiorno(persona, g).filter(c => c.data === g.data);
  const turni = g.turni.filter(t => !t.giorno_prima && t.visibile);
  const presenze = g.presenze.filter(p => !p.giorno_prima);
  // come i turni: il permesso del notturno del giorno prima sta sulla riga di quel giorno
  const giustificativi = g.giustificativi.filter(j => !diUnAltroGiorno(j));
  const scostamento = scostamentoGiorno(g);
  const vuoto = !turni.length && !presenze.length && !giustificativi.length;

  return (
    <tr className={classi || undefined}>
      <td>
        <div className="nome-giorno">
          {m.format("D")}
          <span>
            {m.format("ddd")}
            {g.bloccato && (
              <>
                {" "}
                <LockOutlined aria-label="Giorno bloccato" />
              </>
            )}
          </span>
        </div>
      </td>
      <td>
        <div className="pillole">
          {turni.map(t => {
            const e = etichettaStatoTurno(t);
            const testo = t.lavorativo ? orario(t.ora_inizio, t.ora_fine) : e.testo;
            return (
              <span
                key={t.id}
                className={`pillola ${
                  t.stato === "mancata_timbratura" ? "rosso" : t.lavorativo ? "blu" : "grigio"
                }`}
                title={`${testo}: ${e.testo.toLowerCase()}`}
              >
                {t.stato === "mancata_timbratura" ? `Non timbrato ${testo}` : testo}
              </span>
            );
          })}
          {!turni.length && (
            <span className="attenuato">
              {g.festivita ? g.festivita : !g.previsto ? "Non previsto" : TRATTINO}
            </span>
          )}
        </div>
      </td>
      <td>
        <div className="pillole">
          {presenze.map(p => {
            const livello = livelloPresenza(g, p.id);
            return (
              <span
                key={p.id}
                className={`pillola ${
                  livello === "anomalia"
                    ? "rosso"
                    : livello === "avviso"
                    ? "ambra"
                    : eStraordinario(p)
                    ? "viola"
                    : p.aperta
                    ? "verde"
                    : "grigio"
                }`}
              >
                {ora(p.entrata)}
                {TRATTINO}
                {p.uscita ? ora(p.uscita) + (p.giorno_dopo ? " +1" : "") : "in corso"}
              </span>
            );
          })}
          {!presenze.length && <span className="attenuato">{TRATTINO}</span>}
        </div>
      </td>
      <td className="num">
        {g.totali.minuti_lavorati ? formatDurata(g.totali.minuti_lavorati, unita) : TRATTINO}
      </td>
      <td className="num">
        {scostamento.presenti ? (
          <span className={scostamento.rifiutate && !scostamento.minuti ? "barrato" : undefined}>
            {formatDurata(scostamento.minuti, unita, true)}
            {scostamento.inAttesa && (
              <div className="attenuato" style={{ fontSize: 12 }}>
                da approvare
              </div>
            )}
          </span>
        ) : (
          TRATTINO
        )}
      </td>
      <td>
        <div className="pillole">
          {giustificativi.map(j => (
            <span
              key={j.richiesta_id}
              className={`giust${j.stato === "in_attesa" ? " in-attesa" : ""}`}
              style={{ background: tinta(j.colore, 0.2), borderColor: tinta(j.colore, 0.65) }}
              title={j.stato === "in_attesa" ? "In attesa" : "Accettata"}
            >
              <span className="pallino" style={{ background: j.colore || "#E59E6E" }} />
              {j.unita === "ORE" && j.dalle && j.alle
                ? `${j.nome} ${orario(j.dalle, j.alle)}`
                : j.nome}
            </span>
          ))}
        </div>
      </td>
      <td>
        <div className="pillole">
          {contesti.map(c => (
            <span
              key={c.chiave}
              className={`chip ${c.livello === "anomalia" ? "rosso" : "ambra"}`}
              style={{ cursor: "default" }}
            >
              {chipSegnalazione(c, modalita, unita)}
            </span>
          ))}
        </div>
      </td>
      <td>
        {!vuoto || g.data <= oggi ? (
          <Button
            size="small"
            type="link"
            onClick={onApri}
            aria-label={`Apri la giornata del ${m.format("D MMMM")}`}
          >
            Apri
          </Button>
        ) : null}
      </td>
    </tr>
  );
};
